import supabase from '../lib/supabase';

/**
 * Check if a user has an active subscription
 * This implements the hybrid approach that checks for:
 * 1. Admin status
 * 2. Lifetime access
 * 3. Promotional subscription
 * 4. Regular Stripe subscription
 * 
 * @param {string} userId - The user ID to check
 * @returns {Promise<Object>} - Object with subscription status and details
 */
export const checkSubscription = async (userId) => {
  console.log('checkSubscription: Checking subscription for user ID:', userId);
  if (!userId) {
    console.log('checkSubscription: No user ID provided');
    return { isSubscribed: false, plan: 'free' };
  }
  
  try {
    console.log('checkSubscription: Checking admin access for user ID:', userId);
    
    // First check for special cases in profiles
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('is_admin, has_lifetime_access, promo_subscription_end_date')
      .eq('id', userId)
      .single();
      
    if (profileError) {
      console.error('Error checking profile subscription status:', profileError);
      console.log('checkSubscription: Error accessing profile, denying access');
      return { 
        isSubscribed: false, 
        plan: 'free',
        type: 'error',
        isAdmin: false
      };
    }
    
    // Check if the user is an admin
    if (profile?.is_admin) {
      console.log('checkSubscription: User has admin access');
      return { 
        isSubscribed: true, 
        plan: 'pro',
        type: 'admin',
        isAdmin: true
      };
    }
    
    // Check for lifetime access
    if (profile?.has_lifetime_access) {
      console.log('checkSubscription: User has lifetime access');
      return { 
        isSubscribed: true, 
        plan: 'pro',
        type: 'lifetime',
        isAdmin: false
      };
    }
    
    // Check for promotional subscription
    if (profile?.promo_subscription_end_date) {
      const endDate = new Date(profile.promo_subscription_end_date);
      const now = new Date();
      
      if (endDate > now) {
        console.log(`checkSubscription: User has promotional access until ${endDate.toISOString()}`);
        return { 
          isSubscribed: true, 
          plan: 'pro',
          type: 'promotional',
          endDate: endDate.toISOString(),
          isAdmin: false
        };
      } else {
        console.log(`checkSubscription: User's promotional access expired on ${endDate.toISOString()}`);
      }
    }
    
    // Now check regular subscriptions table
    try {
      const { data: subscription, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();
        
      if (subscriptionError) {
        console.error('Error checking subscription status:', subscriptionError);
        // Continue to beta access fallback
      } else if (subscription) {
        // If it's a custom subscription without Stripe
        if (subscription.is_custom) {
          if (!subscription.end_date || new Date(subscription.end_date) > new Date()) {
            console.log('checkSubscription: User has custom subscription');
            return { 
              isSubscribed: true, 
              plan: subscription.plan_id,
              type: 'custom',
              endDate: subscription.end_date,
              isAdmin: false
            };
          }
        }
        
        // If it's a Stripe subscription
        if (subscription.stripe_subscription_id) {
          console.log('checkSubscription: User has Stripe subscription');
          return { 
            isSubscribed: true, 
            plan: subscription.plan_id,
            type: 'stripe',
            stripeId: subscription.stripe_subscription_id,
            isAdmin: false
          };
        }
      }
    } catch (error) {
      console.error('Error in subscription check:', error);
      // Continue to beta access fallback
    }
    
    // No beta access fallback - users must have a valid subscription
    console.log('checkSubscription: No valid subscription found');
    return { 
      isSubscribed: false, 
      plan: 'free',
      type: 'none',
      isAdmin: false
    };
    
    // No active subscription found
    return { isSubscribed: false };
  } catch (error) {
    console.error('Unexpected error checking subscription:', error);
    return { isSubscribed: false, error };
  }
};

/**
 * Check if a user has admin access
 * 
 * @param {string} userId - The user ID to check
 * @returns {Promise<boolean>} - True if user has admin access
 */
export const checkAdminAccess = async (userId) => {
  console.log('checkAdminAccess: Checking admin access for user ID:', userId);
  if (!userId) {
    console.log('checkAdminAccess: No user ID provided');
    return false;
  }
  
  try {
    // First, directly check if the user exists in profiles
    console.log('checkAdminAccess: Checking if user exists in profiles table');
    const { data: profileExists, error: profileExistsError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId);
      
    console.log('checkAdminAccess: Profile exists check result:', { profileExists, profileExistsError });
    
    if (profileExistsError) {
      console.error('checkAdminAccess: Error checking if profile exists:', profileExistsError);
    }
    
    if (!profileExists || profileExists.length === 0) {
      console.log('checkAdminAccess: Profile does not exist for user ID:', userId);
      console.log('checkAdminAccess: Creating profile for user');
      
      // Try to create a profile for the user
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({ id: userId, is_admin: false, has_lifetime_access: false })
        .single();
        
      if (insertError) {
        console.error('checkAdminAccess: Error creating profile:', insertError);
      } else {
        console.log('checkAdminAccess: Profile created successfully');
      }
    }
    
    // Now check for admin status
    console.log('checkAdminAccess: Querying profiles table for admin status');
    const { data, error } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', userId)
      .single();
    
    console.log('checkAdminAccess: Admin status query result:', { data, error });
      
    if (error) {
      console.error('checkAdminAccess: Error in admin status query:', error);
      return false;
    }
    
    if (!data) {
      console.log('checkAdminAccess: No data returned from admin status query');
      return false;
    }
    
    // Check if the user is an admin based on the database value
    console.log('checkAdminAccess: Checking if user is an admin based on database value');
    console.log('checkAdminAccess: Raw data from database:', data);
    
    // Check if the user is an admin based on the database value
    const isAdmin = data.is_admin === true;
    
    console.log('checkAdminAccess: User admin status from database:', isAdmin);
    
    return isAdmin;
  } catch (error) {
    console.error('checkAdminAccess: Unexpected error:', error);
    return false;
  }
};

export default {
  checkSubscription,
  checkAdminAccess
};
